import React from "react";
import { Helmet } from "react-helmet";
import Header from "../../Header";
import Footer from "../../Footer";
import "./MobilePcConsoleGames.css";

export default function MobilePcConsoleGames() {
    const platforms = [
        {
            title: "Mobile Games",
            description: "Android and iOS games built for smooth controls, optimized performance, store readiness, monetization, analytics, and live updates.",
            points: ["Android & iOS builds", "Touch-first gameplay", "Ads, IAP & analytics"]
        },
        {
            title: "PC Games",
            description: "PC game development with polished gameplay, keyboard/controller support, scalable settings, build packaging, QA, and performance tuning.",
            points: ["Windows builds", "Input support", "Graphics optimization"]
        },
        {
            title: "Web Games",
            description: "Browser-ready games for WebGL and HTML5 experiences with fast loading, responsive UI, backend integration, and cross-device access.",
            points: ["WebGL & HTML5", "Fast loading", "Responsive browser play"]
        },
        {
            title: "Console Games",
            description: "Console-focused development support for controller UX, performance budgets, build preparation, platform rules, and certification readiness.",
            points: ["Controller gameplay", "Platform guidelines", "Release preparation"]
        }
    ];

    const services = [
        "Unity Game Development",
        "Unreal Engine Development",
        "2D & 3D Gameplay",
        "Cross-Platform Porting",
        "Multiplayer Integration",
        "Game Backend",
        "UI/UX & Game UI",
        "2D/3D Art Assets",
        "Animation & VFX",
        "QA & Device Testing",
        "Store Publishing",
        "LiveOps Support"
    ];

    const process = [
        { step: "01", title: "Platform Planning", description: "We define target platforms, game engine, performance goals, controls, monetization, backend needs, and release scope." },
        { step: "02", title: "Prototype & UX", description: "We validate the gameplay loop, controls, camera, UI flow, platform experience, and technical feasibility." },
        { step: "03", title: "Production", description: "Our team builds gameplay systems, art, UI, animation, backend integrations, platform builds, and content pipelines." },
        { step: "04", title: "Optimization & QA", description: "We test devices, memory, frame rate, loading, controls, UI responsiveness, crashes, store rules, and release stability." },
        { step: "05", title: "Launch & LiveOps", description: "We support publishing, build updates, analytics, bug fixes, content updates, monetization, and post-launch improvements." }
    ];

    const benefits = [
        { icon: "fa-solid fa-layer-group", title: "One Game, Multiple Platforms", description: "Plan your game once and prepare platform-specific builds for mobile, web, PC, and console targets." },
        { icon: "fa-solid fa-gauge-high", title: "Performance-Focused Delivery", description: "We optimize controls, rendering, loading, memory, UI behavior, and platform-specific gameplay experience." },
        { icon: "fa-solid fa-server", title: "Backend & Live Systems", description: "Add accounts, leaderboards, multiplayer, analytics, admin tools, economy systems, and live operations." },
        { icon: "fa-solid fa-clipboard-check", title: "QA Before Release", description: "We test gameplay, devices, builds, store requirements, edge cases, monetization, and launch readiness." }
    ];

    const faqs = [
        {
            question: "Can Oengines build one game for mobile, PC, web, and console?",
            answer: "Yes. We can plan cross-platform development and prepare platform-specific builds, controls, UI, optimization, QA, and publishing support."
        },
        {
            question: "Which game engines do you use?",
            answer: "We work with Unity and Unreal Engine, and can also support WebGL, HTML5, backend integrations, cloud services, and platform-specific tooling."
        },
        {
            question: "Do you handle game porting?",
            answer: "Yes. We can help port or adapt games across Android, iOS, web, PC, and console targets with UI, controls, and performance adjustments."
        },
        {
            question: "Can you support the game after launch?",
            answer: "Yes. We provide updates, bug fixing, analytics review, content updates, monetization improvements, store support, and live operations."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Mobile, PC, Web and Console Game Development",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Cross-Platform Game Development",
        "areaServed": "Worldwide",
        "description": "Game development services for mobile, PC, web, and console platforms with Unity, Unreal, backend, QA, publishing, and live support.",
        "offers": platforms.map((platform) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": platform.title,
                "description": platform.description
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>Mobile, PC, Web & Console Game Development Company | Oengines</title>
                <meta name="description" content="Oengines Games LLP builds mobile, PC, web, and console games with Unity, Unreal, 2D/3D gameplay, backend, QA, optimization, publishing, and live operations support." />
                <meta name="keywords" content="mobile game development, PC game development, web game development, console game development, cross platform game development, Unity game development, Unreal Engine game development" />
                <link rel="canonical" href="https://www.oengines.com/mobile-pc-console-games" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="platform-games-page">
                <section className="platform-games-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <span className="platform-games-eyebrow">Cross-Platform Game Development</span>
                                <h1>Mobile, PC, Web & Console Game Development</h1>
                                <p>Build production-ready games for multiple platforms with Oengines Games LLP. We handle gameplay engineering, UI/UX, 2D/3D assets, backend systems, QA, optimization, publishing, and live product support.</p>
                                <div className="platform-games-actions">
                                    <a href="/contact" className="as-btn">Start a Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="platform-games-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="platform-games-hero-card">
                                    <img src="assets/img/GameDevelopment/unity3D-game-development.png" alt="Mobile PC web and console game development" />
                                    <div>
                                        <strong>Cross-Platform Delivery</strong>
                                        <span>Mobile, WebGL, PC, console-ready UX, backend, QA, and LiveOps.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="platform-games-section space">
                    <div className="container">
                        <div className="platform-games-heading text-center">
                            <span>Platforms We Build For</span>
                            <h2>Game Development for Every Major Player Platform</h2>
                            <p>Each platform needs the right controls, UI, performance profile, store flow, testing plan, and launch preparation. We build with those differences in mind from the start.</p>
                        </div>
                        <div className="row g-4">
                            {platforms.map((platform) => (
                                <div className="col-lg-3 col-md-6" key={platform.title}>
                                    <article className="platform-games-card">
                                        <h3>{platform.title}</h3>
                                        <p>{platform.description}</p>
                                        <ul>
                                            {platform.points.map((point) => <li key={point}>{point}</li>)}
                                        </ul>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="platform-games-tech-section space">
                    <div className="container">
                        <div className="platform-games-tech-panel">
                            <div className="platform-games-heading mb-0">
                                <span>What We Deliver</span>
                                <h2>Complete Production Support for Cross-Platform Games</h2>
                                <p>From the core game build to backend, art, testing, publishing, and live updates, our team supports the full product lifecycle.</p>
                            </div>
                            <div className="platform-games-tech-list">
                                {services.map((service) => <span key={service}>{service}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="platform-games-process-section space">
                    <div className="container">
                        <div className="platform-games-heading text-center">
                            <span>Development Process</span>
                            <h2>From Platform Strategy to Launch-Ready Builds</h2>
                        </div>
                        <div className="platform-games-process-grid">
                            {process.map((item) => (
                                <article className="platform-games-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="platform-games-benefit-section space">
                    <div className="container">
                        <div className="platform-games-heading text-center">
                            <span>Why Choose Oengines</span>
                            <h2>Game Delivery Built for More Than One Screen</h2>
                        </div>
                        <div className="row g-4">
                            {benefits.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="platform-games-benefit-card">
                                        <span><i className={item.icon}></i></span>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="platform-games-faq-section space">
                    <div className="container">
                        <div className="platform-games-heading text-center">
                            <span>FAQ</span>
                            <h2>Mobile, PC, Web & Console Game Development Questions</h2>
                        </div>
                        <div className="platform-games-faq-grid">
                            {faqs.map((item) => (
                                <article className="platform-games-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="platform-games-cta">
                    <div className="container">
                        <div className="platform-games-cta-box">
                            <div>
                                <span>Build Across Platforms</span>
                                <h2>Plan your next mobile, PC, web, or console game with Oengines.</h2>
                                <p>Share your game idea, target platforms, engine preference, and launch goals. We will help define the right production path.</p>
                            </div>
                            <a href="/contact" className="as-btn">Book Consultation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
